import {
  Box,
  Container,
  Grid,
  GridItem,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { GlobalUtils } from "src/utils/globalUtils";
import { RootState } from "src/store/store";
import type { MarriageProfileDto } from "src/types/Profile";
import type { MarriageProfileSection } from "src/types/marriageProfileSectionEdit.types";
import MarriageProfileSectionEditModal from "../components/MarriageProfileSectionEditModal";
import ChangeReasonModal from "../components/ChangeReasonModal";
import { MarriageProfileService } from "src/rest/marriageProfileService";
import type { MatrimonyProfileDetailsData } from "src/types/matrimonyProfileDetails";
import ProfileHeaderCard from "src/pages/matrimony/components/ProfileHeaderCard";
import { ProfileService } from "src/rest/profileService";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";
import { useLocation, useNavigate } from "react-router-dom";
import { ConnectionsService } from "src/rest/connectionsService";
import { AppRoutes } from "src/common/constants/constantEnums";
import { UseAppToast } from "src/common/useAppToast";
import { MKS_DAILY_INTEREST_LIMIT } from "src/utils/env";

import {
  AboutSection,
  AstroSection,
  FamilySection,
  LifestyleSection,
  PersonalSection,
  ProfessionalSection,
} from "../components/sections";

import {
  type PhotoState,
  buildSectionInitialValues,
  mapDtoToPageData,
  normaliseSocialLinks,
} from "./matrimonyProfileDetails.utils";

// ── Component ────────────────────────────────────────────────────────

export default function MatrimonyProfileDetailsPage({
  data,
  onConnectNow,
  onLike,
  onShare,
}: {
  data?: MatrimonyProfileDetailsData;
  onConnectNow?: () => void;
  onLike?: () => void;
  onShare?: () => void;
}) {
  // ── URL params ──────────────────────────────────────────────────────
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const idFromQuery = qs.get("id");
  const fromConnections = qs.get("fromConnections") === "1";
  const requestProfileId = qs.get("requestProfileId");
  const yourProfileId = qs.get("yourProfileId");

  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = UseAppToast();

  // ── Active profile (consultant profile switcher) ────────────────────
  const activeMarriageProfileId = useSelector((s: RootState) => s.globalInfo.activeMarriageProfileId);
  const token = GlobalUtils.getToken();
  const isConsultant = token ? GlobalUtils.getAppRoleFromToken(token) === "CONSULTANT" : false;

  /**
   * When a consultant switches the active profile while already on this page,
   * replace the URL ?id= so idFromQuery updates and the existing load effect
   * re-fetches the newly selected profile automatically.
   */
  useEffect(() => {
    if (!isConsultant || !activeMarriageProfileId) return;
    // Don't interfere with connection-flow URLs
    if (fromConnections || requestProfileId || yourProfileId) return;
    const currentId = idFromQuery;
    if (currentId != null && String(currentId) === String(activeMarriageProfileId)) return;
    navigate(
      `${AppRoutes.MATRIMONY_PROFILE_DETAILS}?id=${encodeURIComponent(String(activeMarriageProfileId))}`,
      { replace: true }
    );
  // Only run when the active profile id changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMarriageProfileId]);

  // ── Core state ──────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string>("");
  const [marriageDto, setMarriageDto] = useState<MarriageProfileDto | null>(null);
  const [photoState, setPhotoState] = useState<PhotoState | null>(null);
  /** Object URLs for additional gallery photos (fetched with auth). */
  const [additionalPhotoUrls, setAdditionalPhotoUrls] = useState<string[]>([]);

  const [activeValue, setActiveValue] = useState<boolean>(true);
  const [isTogglingActive, setIsTogglingActive] = useState(false);

  /** Toggle-active reason modal state */
  const [pendingToggleValue, setPendingToggleValue] = useState<boolean | null>(null);
  const [isToggleReasonModalOpen, setIsToggleReasonModalOpen] = useState(false);

  const [loggedInMarriageProfileId, setLoggedInMarriageProfileId] = useState<string | null>(null);

  const [sentTodayCount, setSentTodayCount] = useState<number>(0);
  // For consultants use the limit from their login response; regular users use the env value.
  const dailyInterestLimit = isConsultant
    ? (GlobalUtils.getConsultantInfo()?.dailyRequestCountLimit ?? MKS_DAILY_INTEREST_LIMIT)
    : MKS_DAILY_INTEREST_LIMIT;
  const isDailyLimitReached = sentTodayCount >= dailyInterestLimit;

  // ── Edit modal state ────────────────────────────────────────────────
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editSection, setEditSection] = useState<MarriageProfileSection>("PERSONAL");

  // ── Derived / memoised values ───────────────────────────────────────
  const resolvedAdditionalImages = useMemo(() => {
    const images = marriageDto?.images;
    if (Array.isArray(images) && images.length > 0) {
      return images.filter((img) => img && img.fileName);
    }
    return [];
  }, [marriageDto?.images]);

  const headerSocialLinks = useMemo(
    () => normaliseSocialLinks(marriageDto?.socialMediaLinksList),
    [marriageDto],
  );

  const isOwnProfile = useMemo(() => {
    const currentId = marriageDto?.id ?? idFromQuery;
    if (currentId == null) return false;
    // Consultants: compare against the active profile selected in the switcher
    if (isConsultant && activeMarriageProfileId != null) {
      return String(currentId) === String(activeMarriageProfileId);
    }
    if (!loggedInMarriageProfileId) return false;
    return String(currentId) === String(loggedInMarriageProfileId);
  }, [isConsultant, activeMarriageProfileId, loggedInMarriageProfileId, marriageDto?.id, idFromQuery]);

  const isAlreadyConnected = useMemo(() => {
    if (fromConnections) return true;
    if (requestProfileId && yourProfileId) return true;
    return false;
  }, [fromConnections, requestProfileId, yourProfileId]);

  const pageData = useMemo(() => {
    if (data) return data;
    if (marriageDto) return mapDtoToPageData(marriageDto);
    return null;
  }, [data, marriageDto]);

  const pageDataWithPhoto = useMemo(() => {
    if (!pageData) return null;
    if (!photoState?.objectUrl) return pageData;
    return {
      ...pageData,
      summary: { ...pageData.summary, photoUrl: photoState.objectUrl },
    };
  }, [pageData, photoState?.objectUrl]);

  const sectionInitialValues = useMemo(() => {
    if (!pageDataWithPhoto || !marriageDto) return {};
    return buildSectionInitialValues(editSection, pageDataWithPhoto, marriageDto);
  }, [pageDataWithPhoto, editSection, marriageDto]);

  // ── Data fetching ───────────────────────────────────────────────────

  const loadMarriageProfile = async () => {
    try {
      setLoading(true);
      setLoadError("");

      if (fromConnections && requestProfileId && yourProfileId) {
        const dto = (await MarriageProfileService.checkMutualConnection(
          requestProfileId,
          yourProfileId,
        )) as MarriageProfileDto;
        setMarriageDto(dto);
        return;
      }

      let idToLoad: string | number | null = idFromQuery;

      if (!idToLoad) {
        // Consultants: use the actively selected profile from the switcher
        if (isConsultant && activeMarriageProfileId != null) {
          idToLoad = activeMarriageProfileId;
        } else {
          const email = GlobalUtils.getUserLoginInfo()?.emailId;
          if (email) {
            const profile = await ProfileService.getProfile(email);
            const first = profile?.marriageProfiles?.[0];
            if (first?.id != null) idToLoad = first.id;
          }
        }
      }

      if (!idToLoad) {
        setLoadError("No marriage profile found.");
        setMarriageDto(null);
        return;
      }

      const dto = (await MarriageProfileService.getById(idToLoad)) as MarriageProfileDto;
      setMarriageDto(dto);
    } catch (e: any) {
      setLoadError(e?.response?.data?.message || e?.message || "Failed to load marriage profile.");
      setMarriageDto(null);
    } finally {
      setLoading(false);
    }
  };

  // ── Effects ─────────────────────────────────────────────────────────

  /** Fetch sent-today count for daily interest limit via the dedicated daily-count API. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const email = GlobalUtils.getUserLoginInfo()?.emailId;
        if (!email) return;
        const count = await ConnectionsService.getDailyCount(email);
        if (!cancelled) setSentTodayCount(count);
      } catch {
        if (!cancelled) setSentTodayCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [isConsultant, activeMarriageProfileId]);

  /** Resolve logged-in user's own marriage profile id (regular users only). */
  useEffect(() => {
    if (isConsultant) return; // consultants use activeMarriageProfileId directly
    let cancelled = false;
    (async () => {
      try {
        const email = GlobalUtils.getUserLoginInfo()?.emailId;
        if (!email) return;
        const profile = await ProfileService.getProfile(email);
        const firstId = profile?.marriageProfiles?.[0]?.id;
        if (!cancelled) setLoggedInMarriageProfileId(firstId == null ? null : String(firstId));
      } catch {
        if (!cancelled) setLoggedInMarriageProfileId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isConsultant]);

  /** Load marriage profile on mount / query change, and whenever the active profile switches. */
  useEffect(() => {
    if (data) return;
    void loadMarriageProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, idFromQuery, fromConnections, requestProfileId, yourProfileId, activeMarriageProfileId]);

  /** Cleanup photo object URL. */
  useEffect(() => {
    return () => { if (photoState?.objectUrl) URL.revokeObjectURL(photoState.objectUrl); };
  }, [photoState?.objectUrl]);

  /** Sync active toggle from DTO. */
  useEffect(() => {
    if (marriageDto?.active == null) return;
    setActiveValue(Boolean(marriageDto.active));
  }, [marriageDto?.active]);

  /** Fetch protected profile photo. */
  useEffect(() => {
    const fileName = data?.summary?.profilePhotoFileName || marriageDto?.profilePhoto || "";
    if (!fileName) return;

    let cancelled = false;
    (async () => {
      try {
        const objectUrl = await fetchAuthedImageObjectUrl(fileName);
        if (cancelled) { URL.revokeObjectURL(objectUrl); return; }
        setPhotoState({ rawFileName: fileName, objectUrl });
      } catch {
        setPhotoState(null);
      }
    })();
    return () => { cancelled = true; };
  }, [data?.summary?.profilePhotoFileName, marriageDto?.profilePhoto]);

  // Fetch additional gallery photos (authenticated).
  // The backend returns structured image objects via `images` field.
  // We stabilise the dependency on a JSON string to avoid unnecessary re-fetches
  // caused by new array references when the DTO is re-assigned.
  const additionalImageKey = useMemo(() => {
    const images = marriageDto?.images;
    const fileNames = Array.isArray(images) && images.length > 0
      ? images.map((img) => img.fileName).filter(Boolean)
      : [];
    return JSON.stringify(fileNames);
  }, [marriageDto?.images]);

  useEffect(() => {
    const fileNames: string[] = JSON.parse(additionalImageKey);

    if (fileNames.length === 0) {
      setAdditionalPhotoUrls([]);
      return;
    }

    let cancelled = false;
    const urls: string[] = [];

    (async () => {
      for (const name of fileNames) {
        if (!name || cancelled) continue;
        try {
          const url = await fetchAuthedImageObjectUrl(name);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          urls.push(url);
        } catch {
          // skip failed images — don't break the gallery
        }
      }
      if (!cancelled) setAdditionalPhotoUrls(urls);
    })();

    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [additionalImageKey]);

  // Cleanup additional photo URLs on unmount
  useEffect(() => {
    return () => {
      additionalPhotoUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [additionalPhotoUrls]);
  // ── Actions ─────────────────────────────────────────────────────────

  const handleToggleActive = (next: boolean) => {
    if (!marriageDto?.id) return;
    setPendingToggleValue(next);
    setIsToggleReasonModalOpen(true);
  };

  const handleToggleReasonConfirm = async (reason: string) => {
    if (!marriageDto?.id || pendingToggleValue == null) return;

    const next = pendingToggleValue;
    const previous = activeValue;

    setActiveValue(next);
    setIsTogglingActive(true);
    setIsToggleReasonModalOpen(false);

    try {
      const existingLinks = marriageDto?.socialMediaLinksList;
      const patchData: Record<string, any> = { active: next };

      // WORKAROUND: Backend deserializes the PATCH body into a full DTO.
      // When `socialMediaLinksList` is absent from the JSON, Jackson sets it
      // to null, and the backend wipes the existing child records
      // (JPA orphanRemoval / cascade). Re-include to prevent this.
      // NOTE: `images` are managed via dedicated upload/delete endpoints and
      // should NOT be included in PATCH payloads.
      if (Array.isArray(existingLinks) && existingLinks.length > 0) {
        patchData.socialMediaLinksList = existingLinks;
      }

      const res = await MarriageProfileService.updatePatch(marriageDto.id, {
        data: patchData,
        changeReason: reason,
      });
      const updated = (res as any)?.data;
      if (updated && typeof updated === "object") {
        setMarriageDto(updated as MarriageProfileDto);
      } else {
        setMarriageDto((p) => (p ? ({ ...p, active: next } as any) : p));
      }
      showSuccessToast(next ? "Profile marked Active." : "Profile marked Inactive.");
    } catch (e: any) {
      setActiveValue(previous);
      showErrorToast(e?.response?.data?.message || e?.message || "Please try again.");
    } finally {
      setIsTogglingActive(false);
      setPendingToggleValue(null);
    }
  };

  const sendInterest = async () => {
    if (isDailyLimitReached) return;
    try {
      let senderProfileId: number | string | null = null;

      if (isConsultant && activeMarriageProfileId != null) {
        senderProfileId = activeMarriageProfileId;
      } else {
        const email = GlobalUtils.getUserLoginInfo()?.emailId;
        if (!email) throw new Error("Missing logged-in user email");
        const profile = await ProfileService.getProfile(email);
        senderProfileId = profile?.marriageProfiles?.[0]?.id ?? null;
      }

      if (!senderProfileId) throw new Error("No marriage profile found for this account");

      const receiverProfileId = marriageDto?.id;
      if (!receiverProfileId) throw new Error("Missing receiver profile id");

      const res = await ConnectionsService.sendConnectionRequest(senderProfileId, receiverProfileId);
      const status = (res as any)?.status;
      if (typeof status === "number" && (status < 200 || status >= 300)) {
        throw new Error(`Request failed (HTTP ${status})`);
      }
      showSuccessToast("Interest sent");
      navigate(AppRoutes.MATRIMONY_CONTACT_REQUEST);
    } catch (e: any) {
      showErrorToast(e?.response?.data?.message || e?.message || "Unable to send interest.");
    }
  };

  const handleEdit = (section: MarriageProfileSection) => {
    setEditSection(section);
    setIsEditOpen(true);
  };

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <Box minH="100vh" bg="#FBF7F4">
      <Container maxW="7xl" py={{ base: 3, md: 5, xl: 6 }} px={{ base: 3, md: 6, xl: 8 }}>
        {loading ? (
          <Box py={10} display="flex" justifyContent="center">
            <Spinner />
          </Box>
        ) : loadError ? (
          <Box py={10} textAlign="center">
            <Text color="#6C757D">{loadError}</Text>
          </Box>
        ) : pageDataWithPhoto && marriageDto ? (
          <>
            {/* Header */}
            <ProfileHeaderCard
              data={pageDataWithPhoto.summary}
              additionalPhotoUrls={additionalPhotoUrls}
              socialMediaLinksList={headerSocialLinks}
              onSendInterest={isOwnProfile || isAlreadyConnected || isDailyLimitReached ? undefined : sendInterest}
              onConnectNow={onConnectNow || (() => {})}
              onLike={onLike || (() => {})}
              onShare={onShare || (() => {})}
              onEdit={isOwnProfile ? () => handleEdit("SUMMARY") : undefined}
              onEditSocialLinks={
                isOwnProfile
                  ? () => { setEditSection("PROFESSIONAL"); setIsEditOpen(true); }
                  : undefined
              }
              activeToggle={
                marriageDto.id != null && isOwnProfile
                  ? { value: activeValue, isLoading: isTogglingActive, onChange: handleToggleActive }
                  : undefined
              }
            />

            {/* Section edit modal */}
            {marriageDto.id != null && isOwnProfile ? (
              <>
                <MarriageProfileSectionEditModal
                  isOpen={isEditOpen}
                  onClose={() => setIsEditOpen(false)}
                  section={editSection}
                  profileId={marriageDto.id}
                  initialValues={sectionInitialValues as any}
                  existingSocialMediaLinks={marriageDto.socialMediaLinksList ?? null}
                  existingAdditionalImages={resolvedAdditionalImages}
                  onUpdated={() => void loadMarriageProfile()}
                />
                <ChangeReasonModal
                  isOpen={isToggleReasonModalOpen}
                  onClose={() => {
                    setIsToggleReasonModalOpen(false);
                    setPendingToggleValue(null);
                  }}
                  onConfirm={handleToggleReasonConfirm}
                  isLoading={isTogglingActive}
                  title={
                    pendingToggleValue
                      ? "Reason for Activating Profile"
                      : "Reason for Deactivating Profile"
                  }
                />
              </>
            ) : null}

            {/* Mobile-only: Astro Details immediately after header */}
            <Box display={{ base: "block", md: "none" }} mt={4}>
              <AstroSection
                astro={pageDataWithPhoto.astro}
                dto={marriageDto}
                isOwnProfile={isOwnProfile}
                onEdit={() => handleEdit("ASTRO")}
              />
            </Box>

            {/* Main content grid */}
            <Grid
              mt={{ base: 4, md: 5, xl: 6 }}
              gap={{ base: 4, md: 5, xl: 6 }}
              templateColumns={{ base: "1fr", md: "7fr 3fr" }}
              alignItems="start"
            >
              <GridItem>
                <Stack spacing={{ base: 4, md: 5, xl: 6 }}>
                  <AboutSection
                    about={String(marriageDto.about || "")}
                    isOwnProfile={isOwnProfile}
                    onEdit={() => handleEdit("SUMMARY")}
                  />
                  <PersonalSection
                    personal={pageDataWithPhoto.personal}
                    dto={marriageDto}
                    isOwnProfile={isOwnProfile}
                    onEdit={() => handleEdit("PERSONAL")}
                  />
                  <ProfessionalSection
                    professional={pageDataWithPhoto.professional}
                    dto={marriageDto}
                    isOwnProfile={isOwnProfile}
                    onEdit={() => handleEdit("PROFESSIONAL")}
                  />
                  <FamilySection
                    family={pageDataWithPhoto.family}
                    dto={marriageDto}
                    isOwnProfile={isOwnProfile}
                    onEdit={() => handleEdit("FAMILY")}
                  />
                  <LifestyleSection
                    lifestyle={pageDataWithPhoto.lifestyle}
                    isOwnProfile={isOwnProfile}
                    onEdit={() => handleEdit("LIFESTYLE")}
                  />
                </Stack>
              </GridItem>

              <GridItem>
                <Stack spacing={{ base: 4, md: 5, xl: 6 }}>
                  {/* Desktop-only: Astro in sidebar */}
                  <Box display={{ base: "none", md: "block" }}>
                    <AstroSection
                      astro={pageDataWithPhoto.astro}
                      dto={marriageDto}
                      isOwnProfile={isOwnProfile}
                      onEdit={() => handleEdit("ASTRO")}
                    />
                  </Box>
                </Stack>
              </GridItem>
            </Grid>
          </>
        ) : null}
      </Container>
    </Box>
  );
}